import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileArrowUp, CheckCircle, Queue, WarningCircle, Info, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import ConfettiBurst from "@/components/ConfettiBurst";
import { playApproveChime } from "@/lib/approveSound";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

/**
 * v1.24.11 — Upload Bank Data
 * Admin drops a bank statement XLSX (Date-time | Amount | UTR). Backend
 * auto-verifies matching pending slips and queues the rest for later match.
 */
export default function BankDataUploadDialog({ open, onOpenChange, onDone }) {
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [burst, setBurst] = useState(false); // v1.25.48 — confetti when approvals land
    const inputRef = useRef(null);

    const reset = () => { setFile(null); setResult(null); if (inputRef.current) inputRef.current.value = ""; };

    const submit = async () => {
        if (!file) { toast.error("Choose an .xlsx file first"); return; }
        setBusy(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post("/admin/bank-upload", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(data);
            toast.success(`Approved ${data.approved_count} · Queued ${data.queued_count}`);
            if ((data.approved_count || 0) > 0) {
                setBurst(true); // v1.25.48 — celebrate matches
                playApproveChime(); // v1.25.49 — audio cue on auto-match
            }
            onDone?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
        }
    };

    const close = () => {
        reset();
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
            <ConfettiBurst open={burst} onClose={() => setBurst(false)} pieces={45} />
            <DialogContent className="sm:max-w-2xl" data-testid="bank-upload-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileArrowUp size={20} weight="duotone" className="text-indigo-700" />
                        Upload Bank Data
                    </DialogTitle>
                    <DialogDescription>
                        Drop your bank statement Excel. We'll auto-approve any pending slip whose UTR and amount match — the rest go into a verification queue and auto-match the moment a party posts a slip with that UTR.
                    </DialogDescription>
                </DialogHeader>

                {!result && (
                    <div className="space-y-4">
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600">
                            <div className="flex items-center gap-1.5 font-semibold text-slate-800">
                                <Info size={13} weight="fill" /> Expected columns (header row)
                            </div>
                            <div className="mt-2 font-mono">
                                <div>A: <span className="font-semibold">Date</span> — <span className="text-slate-500">DD/MM/YYYY HH:MM:SS</span></div>
                                <div>B: <span className="font-semibold">Amount</span> — <span className="text-slate-500">e.g. 13500</span></div>
                                <div>C: <span className="font-semibold">UTR</span> — <span className="text-slate-500">e.g. 748368708206</span></div>
                            </div>
                        </div>

                        <div>
                            <input
                                ref={inputRef}
                                type="file"
                                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                data-testid="bank-upload-file-input"
                                className="block w-full cursor-pointer rounded-lg border border-dashed border-slate-300 bg-white p-6 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-indigo-100 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-indigo-800 hover:border-indigo-400"
                            />
                            {file && (
                                <div className="mt-2 flex items-center justify-between rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                                    <span className="truncate"><span className="font-semibold">{file.name}</span> · {(file.size / 1024).toFixed(1)} KB</span>
                                    <button onClick={() => { setFile(null); if (inputRef.current) inputRef.current.value = ""; }} className="text-emerald-800 hover:text-emerald-900" type="button">
                                        <X size={14} />
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={close} disabled={busy}>Cancel</Button>
                            <Button
                                data-testid="bank-upload-submit"
                                onClick={submit}
                                disabled={busy || !file}
                                className="bg-indigo-700 text-white hover:bg-indigo-800"
                            >
                                <FileArrowUp size={14} className="mr-1.5" />
                                {busy ? "Processing…" : "Upload & Match"}
                            </Button>
                        </div>
                    </div>
                )}

                {result && (
                    <div className="space-y-4" data-testid="bank-upload-result">
                        <div className="grid grid-cols-3 gap-2">
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                                <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-800">
                                    <CheckCircle size={11} weight="fill" /> Auto-approved
                                </div>
                                <div data-testid="result-approved-count" className="mt-1 font-display text-2xl font-bold text-emerald-900 tabular-nums">{result.approved_count}</div>
                                <div className="text-[10px] text-emerald-700/70">slips matched & verified</div>
                            </div>
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-amber-800">
                                    <Queue size={11} weight="fill" /> Sent to queue
                                </div>
                                <div data-testid="result-queued-count" className="mt-1 font-display text-2xl font-bold text-amber-900 tabular-nums">{result.queued_count}</div>
                                <div className="text-[10px] text-amber-700/70">will auto-match next slip</div>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                                    <WarningCircle size={11} weight="fill" /> Skipped
                                </div>
                                <div className="mt-1 font-display text-2xl font-bold text-slate-800 tabular-nums">{result.skipped_count + result.already_queued_count}</div>
                                <div className="text-[10px] text-slate-500">already verified / queued / bad row</div>
                            </div>
                        </div>

                        {result.approved.length > 0 && (
                            <div>
                                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-800">Auto-approved slips</div>
                                <div className="max-h-40 overflow-y-auto rounded-lg border border-emerald-200">
                                    <table className="w-full text-xs">
                                        <thead className="bg-emerald-50 text-[10px] uppercase tracking-wider text-emerald-800">
                                            <tr><th className="px-2 py-1 text-left">UTR</th><th className="px-2 py-1 text-left">Party</th><th className="px-2 py-1 text-right">Amount</th></tr>
                                        </thead>
                                        <tbody>
                                            {result.approved.map((r, i) => (
                                                <tr key={i} className="border-t border-emerald-100">
                                                    <td className="px-2 py-1 font-mono">{r.utr}</td>
                                                    <td className="px-2 py-1">{r.party_name}</td>
                                                    <td className="px-2 py-1 text-right tabular-nums">{fmt(r.amount)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        {result.queued.length > 0 && (
                            <div>
                                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-amber-800">Parked in Verification Queue</div>
                                <div className="max-h-40 overflow-y-auto rounded-lg border border-amber-200">
                                    <table className="w-full text-xs">
                                        <thead className="bg-amber-50 text-[10px] uppercase tracking-wider text-amber-800">
                                            <tr><th className="px-2 py-1 text-left">UTR</th><th className="px-2 py-1 text-right">Amount</th></tr>
                                        </thead>
                                        <tbody>
                                            {result.queued.map((r, i) => (
                                                <tr key={i} className="border-t border-amber-100">
                                                    <td className="px-2 py-1 font-mono">{r.utr}</td>
                                                    <td className="px-2 py-1 text-right tabular-nums">{fmt(r.amount)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={reset}>Upload another</Button>
                            <Button data-testid="bank-upload-close" onClick={close} className="bg-indigo-700 text-white hover:bg-indigo-800">Done</Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
